import React, {
  MutableRefObject,
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useController} from 'react-hook-form';
import {mergeProps, useObjectRef} from '@react-aria/utils';
import {mergeRefs} from 'react-merge-refs';
import {BaseFieldPropsWithDom} from '../base-field-props';
import {getInputFieldClassNames} from '../get-input-field-class-names';
import {Field} from '../field';
import {useField} from '../use-field';
import 'intl-tel-input/build/css/intlTelInput.css';
import './phone-field.css';
import {IntlTelInputRef} from 'intl-tel-input/react';
import type IntlTelInputComponent from 'intl-tel-input/reactWithUtils';
import {message} from '@common/i18n/message';
import {useTrans} from '@common/i18n/use-trans';
import {useSelectedLocale} from '@common/i18n/selected-locale';
import clsx from 'clsx';
import {
  PhoneNumberUtils,
  isMobileNumber,
  normalizeCountries,
  resolveTypedPhone,
} from './resolve-typed-phone';

const errorMap = [
  'Invalid number',
  'Invalid country code',
  'Too short',
  'Too long',
  'Invalid number',
];

// Country the field starts on when neither the caller nor the allowed
// countries name one. A field with no country cannot read a number typed
// without its country code ("0501234567") at all.
const FALLBACK_COUNTRY = 'sa';

// above this many countries the list gets a search box
const SEARCHABLE_COUNTRY_COUNT = 8;

// what the phone library gives beyond the widget itself; filled when the
// widget's chunk has loaded, i.e. before any field can be typed into
const phoneLibrary: {
  utils?: PhoneNumberUtils;
  dialCodes: Record<string, string>;
  arabic?: Record<string, string>;
} = {dialCodes: {}};

const IntlTelInput = lazy(async () => {
  const [module, arabic] = await Promise.all([
    import('intl-tel-input/reactWithUtils'),
    // country names for the list and its search; the field works without them
    import('intl-tel-input/i18n/ar').catch(() => null),
  ]);
  phoneLibrary.utils = module.intlTelInput.utils as unknown as
    | PhoneNumberUtils
    | undefined;
  module.intlTelInput.getCountryData().forEach(country => {
    phoneLibrary.dialCodes[country.iso2] = country.dialCode;
  });
  if (arabic) {
    phoneLibrary.arabic = {
      ...(arabic.default as Record<string, string>),
      searchPlaceholder: 'ابحث عن دولة',
    };
  }
  const Widget = module.default;
  // The field decides its options before this chunk has loaded, when the
  // Arabic names are not there yet; this wrapper only ever renders after.
  const LoadedWidget = forwardRef<
    IntlTelInputRef,
    React.ComponentProps<typeof Widget> & {arabicNames?: boolean}
  >(({arabicNames, initOptions, ...widgetProps}, ref) => (
    <Widget
      ref={ref}
      {...widgetProps}
      initOptions={
        arabicNames && phoneLibrary.arabic
          ? {...initOptions, i18n: phoneLibrary.arabic}
          : initOptions
      }
    />
  ));
  return {default: LoadedWidget};
}) as unknown as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof IntlTelInputComponent> & {
    arabicNames?: boolean;
  } & React.RefAttributes<IntlTelInputRef>
>;

export interface PhoneFieldValidity {
  valid: boolean;
  // index into the field's error messages; null when valid
  errorCode: number | null;
  // whether the person typed into the field (as opposed to a value set by code)
  typed: boolean;
}

export interface PhoneFieldProps
  extends BaseFieldPropsWithDom<HTMLInputElement> {
  inputRef?: React.Ref<HTMLInputElement>;
  value?: string;
  onChange?: (value: any) => void;
  onChangeValidity?: (value: any) => void;
  onChangeErrorCode?: (value: any) => void;
  onlyCountries?: string[];
  preferredCountries?: string[];
  excludeCountries?: string[];
  initialCountry?: string;
  /**
   * Countries a number typed without its country code may belong to. When the
   * number is not one of the selected country's but is a mobile in exactly one
   * of these, the flag follows it. Defaults to `onlyCountries`.
   */
  candidateCountries?: string[];
  /** Accept only a real mobile number: one a one-time code can be sent to. */
  mobileOnly?: boolean;
  /**
   * Filled with a function that reports whether what is in the field right
   * now is acceptable (null until the widget has loaded).
   */
  validityRef?: MutableRefObject<(() => PhoneFieldValidity | null) | null>;
}

export const PhoneField = forwardRef<HTMLDivElement, PhoneFieldProps>(
  (
    {
      inputRef,
      inputTestId,
      value,
      onChange,
      onChangeValidity,
      onChangeErrorCode,
      onlyCountries,
      preferredCountries,
      excludeCountries,
      initialCountry,
      candidateCountries,
      mobileOnly = false,
      validityRef,
      ...props
    },
    ref,
  ) => {
    const {localeCode} = useSelectedLocale();
    const isArabic = `${localeCode}`.toLowerCase().startsWith('ar');

    const allowed = normalizeCountries(onlyCountries);
    const excluded = normalizeCountries(excludeCountries);
    // what the widget itself lists: the allowed countries when there are
    // any, and only otherwise every country but the excluded ones
    const offered = (iso2: string) =>
      allowed.length ? allowed.includes(iso2) : !excluded.includes(iso2);
    // 'auto' asks the widget to detect the country by IP, which this site
    // does not configure: it would start with no country at all
    const requestedCountry =
      initialCountry && initialCountry !== 'auto'
        ? normalizeCountries([initialCountry])[0]
        : undefined;
    const startCountry =
      [requestedCountry, ...allowed, FALLBACK_COUNTRY].find(
        (iso2): iso2 is string => !!iso2 && offered(iso2),
      ) ?? FALLBACK_COUNTRY;
    const candidates = (
      candidateCountries ? normalizeCountries(candidateCountries) : allowed
    ).filter(offered);
    // the widget reads its options once, when it is created
    const widgetKey = [
      startCountry,
      allowed.join(','),
      excluded.join(','),
      isArabic ? 'ar' : 'en',
    ].join('|');

    const inputObjRef = useObjectRef(inputRef);
    const {fieldProps, inputProps} = useField<HTMLInputElement>({
      ...props,
      focusRef: inputObjRef,
    });

    const inputFieldClassNames = getInputFieldClassNames({
      ...props,
    });
    const phoneInputClassName = clsx(
      'phone-field-ltr-input',
      '!h-auto !rounded-[10px] !border-[1.5px] !py-12 !shadow-none focus:!ring-0',
      props.invalid
        ? '!border-danger !bg-danger/4 !font-bold !text-danger focus:!border-danger !shadow-[0_0_0_3px_rgb(239_68_68_/_0.10)]'
        : '!border-divider !bg-paper !text-muted focus:!border-divider',
    );
    inputProps.className = clsx(
      'w-full',
      inputFieldClassNames.input,
      phoneInputClassName,
    );
    inputFieldClassNames.wrapper = clsx(
      inputFieldClassNames.wrapper,
      'phone-field-ltr',
      props.invalid
        ? '[&_.iti__selected-country-primary]:border-danger-lighter'
        : '[&_.iti__selected-country-primary]:border-divider',
      '[&_.iti__arrow]:!m-0 [&_.iti__arrow]:!border-t-text-muted',
      '[&_.iti__flag]:!h-[15px] [&_.iti__flag]:!w-[22px] [&_.iti__flag]:!rounded-2',
    );
    const itiRef = useRef<IntlTelInputRef>(null);
    const typedRef = useRef(false);

    const currentValidity = (): PhoneFieldValidity | null => {
      const instance = itiRef.current?.getInstance();
      if (!instance) {
        return null;
      }
      const utils = phoneLibrary.utils;
      const valid =
        mobileOnly && utils
          ? isMobileNumber(utils, instance.getNumber())
          : !!instance.isValidNumber();
      return {
        valid,
        errorCode: valid ? null : Math.max(0, instance.getValidationError()),
        typed: typedRef.current,
      };
    };

    useEffect(() => {
      if (!validityRef) return;
      validityRef.current = currentValidity;
      return () => {
        validityRef.current = null;
      };
    });

    // Only real typing (or pasting) counts, never a value set by code. The
    // widget rewrites the text as it is typed, which hides the change from
    // React's onChange, so the raw input event is what is listened to - and
    // the widget fires an "input" event of its own whenever a number is set
    // by code (a saved phone shown in the field, a ?phone= link), which must
    // not make that number look typed.
    const handleTyping = (e: React.FormEvent<HTMLInputElement>) => {
      const detail = (e.nativeEvent as CustomEvent<{isSetNumber?: boolean}>)
        .detail;
      if (detail && typeof detail === 'object' && detail.isSetNumber) {
        return;
      }
      typedRef.current = true;
      // A saved number the browser fills in arrives whole, with no key
      // presses (no inputType in Chrome, 'insertReplacementText' in Safari
      // and Firefox) and often without the field ever being focused or left,
      // so treat it as a number the person has finished typing. Later, once
      // the widget has read it.
      const {inputType} = e.nativeEvent as InputEvent;
      if (!inputType || inputType === 'insertReplacementText') {
        window.setTimeout(followTypedCountry);
      }
    };

    // Once the person has finished typing (they leave the field or press
    // Enter) the flag follows the number: a Sudanese mobile typed under the
    // Saudi flag belongs to Sudan. Never while typing: the first nine digits
    // of "966501234567" are a complete Sudanese mobile.
    const followTypedCountry = () => {
      const instance = itiRef.current?.getInstance();
      const typedInput = itiRef.current?.getInput();
      const utils = phoneLibrary.utils;
      if (!instance || !typedInput || !utils) {
        return;
      }
      const resolved = resolveTypedPhone({
        raw: typedInput.value,
        selected: instance.getSelectedCountryData()?.iso2,
        candidates,
        dialCodes: phoneLibrary.dialCodes,
        utils,
      });
      if (resolved) {
        // moves the flag and shows the number the way that country writes it
        instance.setNumber(resolved.e164);
        report(resolved.e164);
      }
    };

    // A number this field has reported that the form has not rendered back
    // yet. Until it has, a sync queued for the older value (below) must not
    // put that older number back into the widget.
    const unrenderedRef = useRef<string | null>(null);
    const report = (phone: string) => {
      unrenderedRef.current = phone;
      onChange?.(phone);
    };

    const handleNumberChange = (e: string) => {
      if (itiRef.current) {
        const instance = itiRef.current.getInstance();
        const formattedNumber = instance?.getNumber();
        // Ensure we're passing the formatted number to onChange
        if (formattedNumber) {
          report(formattedNumber);
        } else {
          // If formatting fails, preserve the visible input value.
          report(itiRef.current.getInput()?.value || e);
        }
      }
    };

    const syncInputValue = useCallback(() => {
      if (unrenderedRef.current !== null) {
        return;
      }
      const input = itiRef.current?.getInput() || inputObjRef.current;
      const nextValue = value == null ? '' : value;
      const instance = itiRef.current?.getInstance();

      if (input && inputObjRef.current !== input) {
        inputObjRef.current = input;
      }

      if (!nextValue && (input?.value || instance?.getNumber())) {
        return;
      }

      if (
        instance &&
        (instance.getNumber() !== nextValue || nextValue.startsWith('+'))
      ) {
        instance.setNumber(nextValue);
      }

      if (input && !nextValue.startsWith('+') && input.value !== nextValue) {
        input.value = nextValue;
      }
    }, [inputObjRef, value]);

    // every render carries the form's newest value, including what this
    // field reported (declared before the sync below, so it runs first)
    useEffect(() => {
      unrenderedRef.current = null;
    });

    useEffect(() => {
      syncInputValue();

      const timeout = window.setTimeout(syncInputValue);
      itiRef.current?.getInstance()?.promise.then(syncInputValue);

      return () => window.clearTimeout(timeout);
    }, [syncInputValue]);

    return (
      <Field ref={ref} fieldClassNames={inputFieldClassNames} {...fieldProps}>
        <Suspense
          fallback={
            <input
              {...(inputProps as any)}
              aria-busy="true"
              disabled
              type="tel"
              value={value || ''}
              readOnly
            />
          }
        >
          {/* The widget moves its input into a wrapper of its own, so React
              can no longer take that input out by itself. Re-creating the
              widget therefore replaces this element (which adds no box to
              the layout), never the input directly. */}
          <div key={widgetKey} className="contents">
            <IntlTelInput
              ref={itiRef}
              initialValue={value}
              arabicNames={isArabic}
              inputProps={
                mergeProps(inputProps, {
                  onInputCapture: handleTyping,
                  onBlur: followTypedCountry,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      followTypedCountry();
                    }
                  },
                }) as any
              }
              initOptions={{
                onlyCountries: allowed,
                initialCountry: startCountry,
                excludeCountries: excluded,
                // a short list needs no search box
                countrySearch:
                  !allowed.length || allowed.length > SEARCHABLE_COUNTRY_COUNT,
              }}
              onChangeNumber={handleNumberChange}
              // the field's own verdict: with mobileOnly a number of the right
              // length is not enough
              onChangeValidity={(isValid: boolean) => {
                onChangeValidity?.(currentValidity()?.valid ?? isValid);
              }}
              onChangeErrorCode={(errorCode: number | null) => {
                const validity = currentValidity();
                onChangeErrorCode?.(validity ? validity.errorCode : errorCode);
              }}
            />
          </div>
        </Suspense>
      </Field>
    );
  },
);

export interface FormPhoneFieldProps extends PhoneFieldProps {
  name: string;
  hasError?: boolean;
}

export const FormPhoneField = forwardRef<HTMLDivElement, FormPhoneFieldProps>(
  ({name, inputRef: externalInputRef, hasError: _hasError, ...props}, ref) => {
    const [isValid, setIsValid] = useState(true);
    const [errorCode, setErrorCode] = useState<number | null>(null);
    const latestValueRef = useRef<string>('');
    const validityRef = useRef<(() => PhoneFieldValidity | null) | null>(null);
    const {trans} = useTrans();

    const {
      field: {onChange, onBlur, value = '', ref: inputRef},
      fieldState: {invalid, error, isTouched},
      formState: {isSubmitting},
    } = useController({
      name,
      rules: {
        validate: {
          phoneValid: value => {
            // What the person typed is judged as it is right now, so
            // pressing Enter before leaving the field cannot submit a
            // number the field would refuse a moment later. A value set by
            // code (a ?phone= link that starts a checkout by itself) keeps
            // the rule below, exactly as before.
            const typed = validityRef.current?.();
            if (typed?.typed && !typed.valid) {
              return trans(message(errorMap[typed.errorCode ?? 0]));
            }
            // Return true only if the number is valid
            if (!isValid) {
              return trans(message?.(errorMap[errorCode || 0]));
            }
            // Also validate that we have a value
            if (!latestValueRef.current?.trim()) {
              return 'Phone number is required';
            }
            return true;
          },
        },
      },
    });

    useEffect(() => {
      latestValueRef.current = value == null ? '' : value;
    }, [value]);

    const handleValidationChange = (
      isNewValid: boolean | undefined,
      errorCode: number | null | undefined,
    ) => {
      if (isNewValid !== undefined) setIsValid(isNewValid || !isTouched);
      if (errorCode !== undefined) setErrorCode(!isTouched ? null : errorCode);

      if (!isSubmitting) {
        // Use the latest value when triggering validation
        onChange(latestValueRef.current);
      }
    };

    const formProps: PhoneFieldProps = {
      onChange: (phoneNumber: string) => {
        // Store the latest value
        latestValueRef.current = phoneNumber;
        onChange(phoneNumber);
      },
      onBlur,
      value: value == null ? '' : value,
      invalid: invalid || !isValid,
      errorMessage:
        error?.message ||
        (errorCode !== null ? trans(message(errorMap[errorCode])) : undefined),
      inputRef: externalInputRef
        ? mergeRefs([inputRef, externalInputRef])
        : inputRef,
      name,
      validityRef,
      onChangeValidity: (isValid: boolean) => {
        handleValidationChange(isValid, undefined);
      },
      onChangeErrorCode: (errorCode: number | null) => {
        handleValidationChange(undefined, errorCode);
      },
    };

    return <PhoneField ref={ref} {...mergeProps(formProps, props)} />;
  },
);
